import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  apiError,
  capaAssignees,
  getCapa,
  getEntityAudit,
  transitionCapa,
  updateCapa,
  uploadCapaEvidence,
  validateRootCause,
} from "../services/AiService";
import { getAllDepartmentsService } from "../services/UsermanagementService";
import { Apinames } from "../utils/Apiname";
import { AuditEntry, CapaAction, CapaStatus, CapaDetail, EffectivenessResult, UserRef } from "../types/ai";
import { downloadSecureFile } from "../utils/secureFile";
import { DateField } from "../component/aces/DateField";
import {
  AiNotice,
  AuditTable,
  CapaChip,
  ClassificationChip,
  ErrorAlert,
  Loading,
  PageShell,
  ReasonModal,
  RiskChip,
  UrgentBanner,
  fmtDate,
  fmtDay,
} from "../component/aces/Shared";

type EditableKey =
  | "immediateContainment" | "correction" | "possibleRootCause" | "correctiveAction" | "preventiveAction"
  | "responsibleFunction" | "responsiblePersonId" | "targetDate" | "implementationEvidenceText" | "effectivenessVerification";

const REASON_ACTIONS: CapaAction[] = ["CLOSE", "REJECT", "REOPEN"];

/** Progress steps; a step is "current" while the CAPA is working towards it. */
const STEPS: { key: string; statuses: CapaStatus[] }[] = [
  { key: "proposed", statuses: ["AI_PROPOSED"] },
  { key: "reviewed", statuses: ["UNDER_REVIEW"] },
  { key: "accepted", statuses: ["ACCEPTED"] },
  { key: "assigned", statuses: ["ASSIGNED"] },
  { key: "inProgress", statuses: ["IN_PROGRESS", "REOPENED"] },
  { key: "implemented", statuses: ["IMPLEMENTED"] },
  { key: "verified", statuses: ["EFFECTIVENESS_VERIFICATION", "EFFECTIVENESS_VERIFIED"] },
  { key: "closed", statuses: ["CLOSED"] },
];

const CapaDetailScreen = () => {
  const { id } = useParams();
  const capaId = Number(id);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [detail, setDetail] = useState<CapaDetail | null>(null);
  const [draft, setDraft] = useState<Partial<Record<EditableKey, any>> | null>(null);
  const [assignees, setAssignees] = useState<UserRef[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [pending, setPending] = useState<CapaAction | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [rootCauseOpen, setRootCauseOpen] = useState(false);
  const [rootCause, setRootCause] = useState("");
  const [effectiveness, setEffectiveness] = useState<{ result: EffectivenessResult; notes: string }>({ result: "EFFECTIVE", notes: "" });
  const [audit, setAudit] = useState<AuditEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await getCapa(capaId));
      setError(null);
    } catch (e) {
      setError(apiError(e));
    }
  }, [capaId]);

  useEffect(() => {
    load();
    capaAssignees().then(setAssignees).catch(() => setAssignees([]));
    getAllDepartmentsService(Apinames.lovDepartments).then((r: any) => setDepartments(r?.result || [])).catch(() => setDepartments([]));
  }, [load]);

  if (!detail) {
    return (
      <PageShell title={t("aces.capa.title")} crumbs={[{ label: t("aces.nav.capa"), to: "/capa" }, { label: t("aces.capa.title") }]}>
        <ErrorAlert message={error} />
        {!error && <Loading />}
      </PageShell>
    );
  }
  const c = detail.capa;
  const value = (k: EditableKey) => (draft && k in draft ? draft[k] : (c as any)[k]);
  const changes = draft
    ? Object.fromEntries(Object.entries(draft).filter(([k, v]) => ((c as any)[k] ?? "") !== (v ?? "")).map(([k, v]) => [k, v === "" ? null : v]))
    : {};
  const overwrites = Object.keys(changes).some((k) => (c as any)[k] != null && (c as any)[k] !== "");

  const save = async (reason?: string) => {
    try {
      setDetail(await updateCapa(capaId, changes, reason || undefined, c.version));
      setDraft(null);
      setSaveOpen(false);
    } catch (e) {
      setError(apiError(e));
      setSaveOpen(false);
    }
  };

  const act = async (action: CapaAction, reason?: string) => {
    setBusy(true);
    try {
      const extra = action === "RECORD_EFFECTIVENESS"
        ? { effectivenessResult: effectiveness.result, effectivenessNotes: effectiveness.notes }
        : { reason };
      setDetail(await transitionCapa(capaId, action, extra));
      setPending(null);
    } catch (e) {
      setError(apiError(e));
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  const confirmRootCause = async (reason: string) => {
    try {
      setDetail(await validateRootCause(capaId, rootCause, reason || undefined));
      setRootCauseOpen(false);
    } catch (e) {
      setError(apiError(e));
      setRootCauseOpen(false);
    }
  };

  const upload = async (file?: File) => {
    if (!file) return;
    setBusy(true);
    try {
      setDetail(await uploadCapaEvidence(capaId, file));
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const text = (k: EditableKey, label: string, rows = 2) => (
    <div className="aces-field" key={k}>
      <label htmlFor={`capa-${k}`}>{label}</label>
      {draft ? (
        <textarea id={`capa-${k}`} className="form-control" rows={rows} value={value(k) || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
      ) : (
        <div className="value">{(c as any)[k] || "—"}</div>
      )}
    </div>
  );

  const currentIndex = STEPS.findIndex((s) => s.statuses.includes(c.status));
  const hasImplementationEvidence = !!c.implementationEvidenceText || detail.evidence.length > 0;
  // What the next transition still needs (the server enforces the same guards).
  const nextStep = (() => {
    switch (c.status) {
      case "AI_PROPOSED": return "review";
      case "UNDER_REVIEW": return "decide";
      case "ACCEPTED": return !c.responsiblePersonId || !c.targetDate ? "assign" : "assignReady";
      case "ASSIGNED": return "start";
      case "IN_PROGRESS": return hasImplementationEvidence ? "implementReady" : "implement";
      case "IMPLEMENTED": return c.effectivenessVerification ? "verifyReady" : "verify";
      case "EFFECTIVENESS_VERIFICATION": return "record";
      case "EFFECTIVENESS_VERIFIED": return c.validatedRootCause ? "closeReady" : "close";
      case "REOPENED": return "reopened";
      default: return null;
    }
  })();
  const active = c.status !== "CLOSED" && c.status !== "REJECTED";
  const missing = active
    ? ([
        [!c.responsiblePersonId, "owner"],
        [!c.targetDate, "targetDate"],
        [!hasImplementationEvidence, "implementation"],
        [!c.effectivenessVerification, "method"],
        [!c.effectivenessResult, "result"],
        [!c.validatedRootCause, "rootCause"],
      ] as [boolean, string][]).filter(([isMissing]) => isMissing).map(([, k]) => k)
    : [];
  const daysOverdue = detail.overdue && c.targetDate
    ? Math.floor((Date.now() - new Date(`${c.targetDate.slice(0, 10)}T00:00:00Z`).getTime()) / 86400000)
    : 0;
  const aiOriginal = detail.aiOriginal;

  return (
    <PageShell
      title={<>{t("aces.capa.title")} <span className="aces-muted" style={{ fontSize: 16 }}>{c.capaRef}</span></>}
      crumbs={[{ label: t("aces.nav.capa"), to: "/capa" }, { label: c.capaRef }]}
      actions={<button className="aces-btn secondary" onClick={() => navigate(`/ai/finding/${c.findingId}`)}><i className="ri-arrow-left-line"></i> {detail.finding.findingRef}</button>}
    >
      <ErrorAlert message={error} onClose={() => setError(null)} />
      {detail.finding.isUrgent && <UrgentBanner />}

      <div className="aces-card">
        {c.status !== "REJECTED" && (
          <ol className="aces-stepper">
            {STEPS.map((s, i) => {
              const done = i < currentIndex || c.status === "CLOSED";
              const current = !done && i === currentIndex;
              return (
                <li key={s.key} className={`step ${done ? "done" : ""} ${current ? "current" : ""}`} aria-current={current ? "step" : undefined}>
                  <span className="marker" aria-hidden="true">{done ? <i className="ri-check-line"></i> : i + 1}</span>
                  {t(`aces.capa.steps.${s.key}`)}
                  {done && <span className="visually-hidden"> ({t("aces.capa.steps.completed")})</span>}
                </li>
              );
            })}
          </ol>
        )}
        <dl className="aces-capa-summary">
          <div><dt>{t("aces.capa.owner")}</dt><dd>{detail.responsiblePerson?.name || c.responsibleFunction || "—"}</dd></div>
          <div><dt>{t("aces.capa.targetDate")}</dt><dd>{fmtDay(c.targetDate)}</dd></div>
          <div><dt>{t("aces.capa.daysOverdue")}</dt><dd className={daysOverdue > 0 ? "aces-overdue" : ""}>{daysOverdue}</dd></div>
          <div><dt>{t("aces.capa.nextAction")}</dt><dd>{nextStep ? t(`aces.capa.nextStep.${nextStep}`) : t("aces.capa.noNextAction")}</dd></div>
          <div><dt>{t("aces.capa.evidenceMissing")}</dt>
            <dd>{missing.length ? <ul className="mb-0">{missing.map((k) => <li key={k}>{t(`aces.capa.missing.${k}`)}</li>)}</ul> : t("aces.capa.noneMissing")}</dd>
          </div>
        </dl>
        <div className="aces-actions" style={{ justifyContent: "space-between" }}>
          <div className="aces-actions">
            <CapaChip value={c.status} />
            <ClassificationChip value={detail.finding.classification} />
            <RiskChip level={detail.finding.riskLevel} score={detail.finding.riskScore} />
            <span className="aces-chip grey">{t(`aces.capa.origin.${c.origin}`)}</span>
            {detail.overdue && <span className="aces-chip red">{t("aces.capa.overdue")}</span>}
          </div>
          {!draft && <div className="aces-actions">
            {detail.canEdit && <button className="aces-btn secondary" onClick={() => setDraft({})}><i className="ri-edit-line"></i> {t("aces.finding.edit")}</button>}
            {detail.allowedActions.filter((a) => a !== "RECORD_EFFECTIVENESS").map((a) => (
              <button key={a} className={`aces-btn ${a === "REJECT" ? "danger" : a === "CLOSE" ? "success" : ""}`} disabled={busy}
                onClick={() => (REASON_ACTIONS.includes(a) ? setPending(a) : act(a))}>
                {t(`aces.capa.actions.${a}`)}
              </button>
            ))}
          </div>}
        </div>
        <p className="aces-muted mt-2 mb-0">{detail.checklist.referenceNo} — {detail.checklist.title} · {detail.finding.statement}</p>
      </div>

      {!draft && detail.allowedActions.includes("RECORD_EFFECTIVENESS") && (
        <div className="aces-card">
          <h5>{t("aces.capa.recordEffectiveness")}</h5>
          <div className="aces-grid-2">
            <div className="aces-field">
              <label>{t("aces.capa.effectivenessResult")}</label>
              <select className="form-control" value={effectiveness.result} onChange={(e) => setEffectiveness({ ...effectiveness, result: e.target.value as EffectivenessResult })}>
                {(["EFFECTIVE", "PARTIALLY_EFFECTIVE", "NOT_EFFECTIVE"] as EffectivenessResult[]).map((r) => <option key={r} value={r}>{t(`aces.enum.effectiveness.${r}`)}</option>)}
              </select>
            </div>
            <div className="aces-field">
              <label>{t("aces.capa.effectivenessNotes")} <span style={{ color: "red" }}>*</span></label>
              <textarea className="form-control" rows={2} value={effectiveness.notes} onChange={(e) => setEffectiveness({ ...effectiveness, notes: e.target.value })} />
            </div>
          </div>
          {effectiveness.result !== "EFFECTIVE" && <p className="aces-muted">{t("aces.capa.notEffectiveHint")}</p>}
          <button className="aces-btn" disabled={busy || !effectiveness.notes.trim()} onClick={() => act("RECORD_EFFECTIVENESS")}>{t("aces.capa.actions.RECORD_EFFECTIVENESS")}</button>
        </div>
      )}

      <div className="aces-grid-2">
        <div className="aces-card">
          <h5>{t("aces.capa.containmentCorrection")}</h5>
          {text("immediateContainment", t("aces.finding.containment"))}
          {text("correction", t("aces.capa.correction"))}
        </div>
        <div className="aces-card">
          <h5>{t("aces.capa.rootCause")}</h5>
          {text("possibleRootCause", t("aces.capa.possibleRootCause"))}
          <div className="aces-field">
            <label>{t("aces.capa.validatedRootCause")}</label>
            <div className="value">
              {c.validatedRootCause ? (
                <>
                  {c.validatedRootCause}
                  <div className="aces-muted">{detail.rootCauseValidatedBy?.name} · {fmtDate(c.rootCauseValidatedAt)}</div>
                </>
              ) : <span className="aces-missing">{t("aces.capa.notValidated")}</span>}
            </div>
          </div>
          {detail.canValidateRootCause && !draft && (
            <button className="aces-btn secondary small" onClick={() => { setRootCause(c.validatedRootCause || c.possibleRootCause || ""); setRootCauseOpen(true); }}>
              <i className="ri-shield-check-line"></i> {t("aces.capa.validateRootCause")}
            </button>
          )}
        </div>
      </div>

      <div className="aces-grid-2">
        <div className="aces-card">
          <h5>{t("aces.capa.actionsTitle")}</h5>
          {text("correctiveAction", t("aces.capa.correctiveAction"))}
          {text("preventiveAction", t("aces.capa.preventiveAction"))}
        </div>
        <div className="aces-card">
          <h5>{t("aces.capa.assignment")}</h5>
          <div className="aces-field">
            <label>{t("aces.capa.responsibleFunction")}</label>
            {draft ? (
              <>
                <input className="form-control" list="aces-departments" value={value("responsibleFunction") || ""} onChange={(e) => setDraft({ ...draft, responsibleFunction: e.target.value })} />
                <datalist id="aces-departments">{departments.map((d) => <option key={d} value={d} />)}</datalist>
              </>
            ) : <div className="value">{c.responsibleFunction || "—"}</div>}
          </div>
          <div className="aces-field">
            <label>{t("aces.capa.responsiblePerson")}</label>
            {draft ? (
              <select className="form-control" value={value("responsiblePersonId") ?? ""} onChange={(e) => setDraft({ ...draft, responsiblePersonId: e.target.value ? Number(e.target.value) : null })}>
                <option value="">—</option>
                {assignees.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            ) : <div className="value">{detail.responsiblePerson?.name || "—"}</div>}
          </div>
          <div className="aces-field">
            <label htmlFor="capa-targetDate">{t("aces.capa.targetDate")}</label>
            {draft ? (
              <DateField id="capa-targetDate" value={value("targetDate") || ""} onChange={(v) => setDraft({ ...draft, targetDate: v })} />
            ) : <div className="value" style={{ color: detail.overdue ? "#b71c1c" : undefined }}>{fmtDay(c.targetDate)}</div>}
          </div>
        </div>
      </div>

      <div className="aces-grid-2">
        <div className="aces-card">
          <h5>{t("aces.capa.implementation")}</h5>
          {text("implementationEvidenceText", t("aces.capa.implementationEvidence"), 3)}
          <div className="aces-field">
            <label>{t("aces.capa.files")}</label>
            {detail.evidence.length === 0 ? <div className="aces-muted">—</div> : (
              <ul className="mb-1">
                {detail.evidence.map((e) => (
                  <li key={e.id}>
                    <button type="button" className="aces-table-link" onClick={() => downloadSecureFile(e.id, e.name || `${e.code}`).catch((err) => setError(apiError(err)))}>{e.code} — {e.name}</button>
                  </li>
                ))}
              </ul>
            )}
            {detail.canEdit && !draft && (
              <>
                <input ref={fileRef} type="file" aria-label={t("aces.capa.upload")} accept="image/*,application/pdf" style={{ display: "none" }} onChange={(e) => upload(e.target.files?.[0])} />
                <button className="aces-btn small secondary" disabled={busy} onClick={() => fileRef.current?.click()}><i className="ri-upload-2-line"></i> {t("aces.capa.upload")}</button>
              </>
            )}
          </div>
        </div>
        <div className="aces-card">
          <h5>{t("aces.capa.effectiveness")}</h5>
          {text("effectivenessVerification", t("aces.capa.effectivenessMethod"))}
          <dl className="aces-kv">
            <dt>{t("aces.capa.effectivenessResult")}</dt>
            <dd>{c.effectivenessResult ? t(`aces.enum.effectiveness.${c.effectivenessResult}`) : "—"}</dd>
            <dt>{t("aces.capa.effectivenessNotes")}</dt>
            <dd>{c.effectivenessNotes || "—"}</dd>
            <dt>{t("aces.capa.verifiedBy")}</dt>
            <dd>{detail.verifiedBy ? `${detail.verifiedBy.name} · ${fmtDate(c.verifiedAt)}` : "—"}</dd>
            <dt>{t("aces.capa.closedBy")}</dt>
            <dd>{detail.closedBy ? `${detail.closedBy.name} · ${fmtDate(c.closedAt)}` : "—"}</dd>
          </dl>
        </div>
      </div>

      {draft && (
        <div className="aces-card aces-actions">
          <button className="aces-btn" disabled={Object.keys(changes).length === 0} onClick={() => (overwrites ? setSaveOpen(true) : save())}>{t("aces.common.save")}</button>
          <button className="aces-btn secondary" onClick={() => setDraft(null)}>{t("common.cancel")}</button>
        </div>
      )}

      {aiOriginal && (
        <div className="aces-card aces-ai-panel">
          <h5><i className="ri-sparkling-2-line" aria-hidden="true"></i> {t("aces.capa.aiProposal")}</h5>
          {c.status === "AI_PROPOSED" && <AiNotice />}
          <dl className="aces-kv">
            {Object.entries(aiOriginal).map(([k, v]) => (<React.Fragment key={k}><dt>{t(`aces.capa.proposal.${k}`)}</dt><dd>{v || "—"}</dd></React.Fragment>))}
          </dl>
        </div>
      )}

      <div className="aces-grid-2">
        <div className="aces-card">
          <h5>{t("aces.finding.history")}</h5>
          <ul className="aces-timeline">
            {detail.history.map((h) => (
              <li key={h.id}><b>{t(`aces.enum.capa.${h.toStatus}`)}</b> · {fmtDate(h.changedAt)} · {h.userName}{h.reason && <div className="aces-muted">{h.reason}</div>}</li>
            ))}
          </ul>
        </div>
        <div className="aces-card">
          <div className="aces-actions" style={{ justifyContent: "space-between" }}>
            <h5 className="mb-0">{t("aces.audit.title")}</h5>
            {audit === null && <button className="aces-btn small secondary" onClick={() => getEntityAudit("CAPA", capaId).then(setAudit).catch((e) => setError(apiError(e)))}>{t("aces.common.show")}</button>}
          </div>
          {audit && <div className="mt-2"><AuditTable entries={audit} /></div>}
        </div>
      </div>

      <ReasonModal show={saveOpen} title={t("aces.finding.saveTitle")} onCancel={() => setSaveOpen(false)} onConfirm={(r) => save(r)} />
      <ReasonModal show={pending !== null} title={pending ? t(`aces.capa.actions.${pending}`) : ""} danger={pending === "REJECT"}
        confirmLabel={pending ? t(`aces.capa.actions.${pending}`) : undefined}
        onCancel={() => setPending(null)} onConfirm={(r) => act(pending!, r)} />
      <ReasonModal show={rootCauseOpen} title={t("aces.capa.validateRootCause")} required={!!c.validatedRootCause}
        onCancel={() => setRootCauseOpen(false)} onConfirm={confirmRootCause}>
        <div className="aces-field">
          <label>{t("aces.capa.validatedRootCause")} <span style={{ color: "red" }}>*</span></label>
          <textarea className="form-control" rows={3} value={rootCause} onChange={(e) => setRootCause(e.target.value)} />
          <div className="aces-muted mt-1">{t("aces.capa.rootCauseHint")}</div>
        </div>
      </ReasonModal>
    </PageShell>
  );
};

export default CapaDetailScreen;
